// Core Firestore adapter utilities for Configuration Page migration
// Provides common patterns for Firestore access with error handling

import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'
import { db } from '../config/firebase.js'

/**
 * Creates a real-time Firestore listener with error handling
 * @param {string} collection - Firestore collection name
 * @param {string} documentId - Document ID
 * @param {function} onData - Callback for data updates
 * @param {function} onError - Callback for errors
 * @returns {function} Unsubscribe function
 */
export const createRealtimeListener = (collection, documentId, onData, onError) => {
  const docRef = doc(db, collection, documentId)
  
  return onSnapshot(
    docRef,
    (docSnap) => {
      if (docSnap.exists()) {
        onData(docSnap.data())
      } else {
        onData(null)
      }
    },
    (error) => {
      console.error(`Firestore listener error for ${collection}/${documentId}:`, error)
      onError(error)
    },
  )
}

/**
 * Updates a Firestore document with automatic timestamping
 * @param {string} collection - Firestore collection name
 * @param {string} documentId - Document ID
 * @param {object} data - Data to update
 * @param {object} options - Update options
 * @returns {Promise} Update promise
 */
export const updateDocument = async (collection, documentId, data, options = {}) => {
  const docRef = doc(db, collection, documentId)
  
  const updateData = {
    ...data,
    lastUpdated: serverTimestamp(),
    ...options.additionalFields,
  }
  
  return setDoc(docRef, updateData, { merge: options.merge !== false })
}

/**
 * Gets a single document from Firestore
 * @param {string} collection - Firestore collection name
 * @param {string} documentId - Document ID
 * @returns {Promise<object|null>} Document data or null if not found
 */
export const getDocument = async (collection, documentId) => {
  const docRef = doc(db, collection, documentId)
  const docSnap = await getDoc(docRef)
  
  return docSnap.exists() ? docSnap.data() : null
}

/**
 * Transforms Firestore deviceInfo to express API format
 * @param {object} firestoreDeviceInfo - Raw Firestore deviceInfo document
 * @returns {object|null} Express-compatible device info or null
 */
export const adaptDeviceInfoForExpress = (firestoreDeviceInfo) => {
  if (!firestoreDeviceInfo) return null
  
  return {
    cpuInfo: firestoreDeviceInfo.cpuInfo,
    diskInfo: firestoreDeviceInfo.diskInfo || [],
    linuxVersion: firestoreDeviceInfo.linuxVersion || 'Unknown',
    regulatorSoftwareVersion: firestoreDeviceInfo.regulatorSoftwareVersion || 'Unknown',
    regulatorHardwareVersion: firestoreDeviceInfo.regulatorHardwareVersion || 'Unknown',
    deviceType: firestoreDeviceInfo.deviceType || 'Unknown',
    ipAddress: firestoreDeviceInfo.ipAddress || 
              firestoreDeviceInfo.networkInfo?.ipAddress || 'Unknown',
    hostname: firestoreDeviceInfo.hostname || 'Unknown',
  }
}

/**
 * Fetches device info from Firestore and transforms it for express compatibility
 * @param {string} deviceId - Device ID (optional, uses CloudContext if not provided)
 * @returns {Promise<object|null>} Express-compatible device info
 */
export const getDeviceInfo = async (deviceId) => {
  // If no deviceId provided, we need to get it from context
  // For now, we'll need to pass it in or handle this at the component level
  if (!deviceId) {
    throw new Error('Device ID is required for getDeviceInfo')
  }
  
  try {
    const firestoreDeviceInfo = await getDocument('deviceInfo', deviceId)
    return adaptDeviceInfoForExpress(firestoreDeviceInfo)
  } catch (error) {
    console.error('Error fetching device info from Firestore:', error)
    throw new Error('Failed to fetch device info')
  }
}
